import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plane, Save, Trash2, Plus, Loader2, KeyRound,
  CircleCheck, CircleX, CircleAlert, ExternalLink, RotateCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { refreshFlights } from "@/components/dashboard/FlightsCard";
import { FlightFetchLog } from "./FlightFetchLog";
import { DataQualityCard } from "./DataQualityCard";
import { ServiceStatusCard } from "./ServiceStatusCard";
import { FlightForensicsPanel } from "./FlightForensicsPanel";

/**
 * Settings → Flyg.
 *
 * Layout:
 *   1. Bevakade flighter (TOP — det användaren använder oftast)
 *   2. Datakällor (Swedavia, adsb.lol, OpenSky, AeroDataBox) med
 *      hälsoindikator, quota-info och API-nyckel-formulär.
 */
export function FlightsSection() {
  const [config, setConfig] = useState(null);
  const [openskyClientId, setOpenskyClientId] = useState("");
  const [openskyClientSecret, setOpenskyClientSecret] = useState("");
  const [savingOpensky, setSavingOpensky] = useState(false);
  const [openskyConfig, setOpenskyConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState({
    flightinfo_primary: "",
    flightinfo_secondary: "",
    waittime_primary: "",
    waittime_secondary: "",
  });
  const [savingKeys, setSavingKeys] = useState(false);
  const [watched, setWatched] = useState([]);
  const [newFlight, setNewFlight] = useState("");
  const [adding, setAdding] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, watch, openskyCfg] = await Promise.all([
        api.get("/swedavia/config"),
        api.get("/flights/watched"),
        api.get("/opensky/config").catch(() => ({ data: null })),
      ]);
      setConfig(cfg.data);
      setWatched(watch.data?.items || []);
      setOpenskyConfig(openskyCfg.data);
    } catch {
      toast.error("Kunde inte hämta konfig");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const { data } = await api.get("/flights/sources/health");
      setHealth(data);
    } catch {
      /* tyst — visas som "okänt" i UI */
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    // Defer state-uppdateringar utanför effect-fasen — undviker React 19:s
    // set-state-in-effect-regel
    queueMicrotask(() => {
      loadAll();
      loadHealth();
    });
  }, [loadAll, loadHealth]);


  const saveOpenskyCreds = async () => {
    const cid = openskyClientId.trim();
    const sec = openskyClientSecret.trim();
    if (!cid && !sec) return toast.error("Fyll i client_id och client_secret");
    setSavingOpensky(true);
    try {
      const { data } = await api.post("/opensky/config", {
        client_id: cid || undefined,
        client_secret: sec || undefined,
      });
      if (data?.verified) {
        toast.success("Verifierad — 4000 anrop/dag aktiverat");
      } else if (data?.saved) {
        toast.warning("Sparat men kunde inte verifiera (kolla credentials)");
      } else {
        toast.success("Sparat");
      }
      setOpenskyClientId("");
      setOpenskyClientSecret("");
      loadAll();
      loadHealth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSavingOpensky(false);
    }
  };

  const removeOpenskyCreds = async () => {
    if (!window.confirm("Ta bort OpenSky-credentials? Du faller då tillbaka till 100 anrop/dag.")) return;
    setSavingOpensky(true);
    try {
      await api.post("/opensky/config", { client_id: null, client_secret: null });
      toast.success("Borttagna");
      loadAll();
      loadHealth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte ta bort");
    } finally {
      setSavingOpensky(false);
    }
  };

  const saveKeys = async () => {
    const payload = {};
    for (const k of Object.keys(keys)) {
      if (keys[k].trim()) payload[k] = keys[k].trim();
    }
    if (Object.keys(payload).length === 0) return toast.error("Inget att spara");
    setSavingKeys(true);
    try {
      await api.post("/swedavia/config", payload);
      toast.success("Nycklar sparade");
      setKeys({ flightinfo_primary: "", flightinfo_secondary: "", waittime_primary: "", waittime_secondary: "" });
      loadAll();
      loadHealth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSavingKeys(false);
    }
  };

  const addFlight = async () => {
    const fid = newFlight.trim();
    if (!fid) return;
    setAdding(true);
    try {
      const { data } = await api.post("/flights/watched", { flight_id: fid });
      toast.success(`Bevakar ${data.watch?.flight_id}`);
      setNewFlight("");
      loadAll();
      // Trigga omedelbar dashboard-refresh så flighten dyker upp direkt
      refreshFlights();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte lägga till");
    } finally {
      setAdding(false);
    }
  };

  const removeFlight = async (id, flightId) => {
    if (!confirm(`Sluta bevaka ${flightId}?`)) return;
    try {
      await api.delete(`/flights/watched/${id}`);
      loadAll();
      refreshFlights();
    } catch {
      toast.error("Kunde inte ta bort");
    }
  };

  return (
    <div className="space-y-5" data-testid="flights-section">
      {/* ============ 1. BEVAKADE FLIGHTER (TOP) ============ */}
      <div className="card-soft p-3 space-y-3" data-testid="watched-flights-card">
        <div className="flex items-center gap-2">
          <Plane size={16} className="text-[color:var(--jarvis)]" />
          <h3 className="text-sm font-semibold">Bevakade flighter</h3>
        </div>
        {watched.length === 0 ? (
          <p className="text-xs text-[color:var(--text-tertiary)]">Inga flighter bevakas.</p>
        ) : (
          <ul className="space-y-2">
            {watched.map((w) => (
              <li
                key={w.id}
                className="flex items-center justify-between p-2 rounded bg-[color:var(--bg-elevated)]"
                data-testid={`settings-flight-${w.flight_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{w.flight_id}</p>
                  <p className="text-[11px] text-[color:var(--text-tertiary)]">
                    {w.airport_iata} · {w.kind === "departure" ? "avgång" : "ankomst"} · {w.scheduled_time?.slice(0, 16).replace("T", " ")}
                  </p>
                </div>
                <button
                  onClick={() => removeFlight(w.id, w.flight_id)}
                  className="p-1.5 rounded hover:bg-white text-[color:var(--danger)]"
                  data-testid={`settings-flight-remove-${w.flight_id}`}
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <Input
            value={newFlight}
            onChange={(e) => setNewFlight(e.target.value.toUpperCase())}
            placeholder="Flightnummer, t.ex. SK1407"
            className="h-9 text-sm font-mono"
            data-testid="flight-add-input"
            onKeyDown={(e) => { if (e.key === "Enter") addFlight(); }}
          />
          <Button
            size="sm"
            onClick={addFlight}
            disabled={adding || !newFlight.trim()}
            data-testid="flight-add-button"
          >
            {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          </Button>
        </div>
        <p className="text-[10px] text-[color:var(--text-tertiary)] leading-snug">
          Tips: Du kan också säga <em>&ldquo;Lägg till flyg SK1407&rdquo;</em> till Edit.
        </p>
      </div>

      {/* ============ 3. DATALOGG (debug-fönster) ============ */}
      <FlightFetchLog />

      {/* ============ 3b. DATA QUALITY (canonical-projektion + coverage) ============ */}
      <DataQualityCard />

      {/* ============ 3c. SERVICE STATUS (auto-disable + manuell test) ============ */}
      <ServiceStatusCard />

      {/* ============ 4. FORENSICS (post-flight rapport + priority-table) ============ */}
      <FlightForensicsPanel />

      {/* ============ 2. DATAKÄLLOR ============ */}
      <div className="card-soft p-3 space-y-3" data-testid="data-sources-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-[color:var(--jarvis)]" />
            <h3 className="text-sm font-semibold">Datakällor</h3>
          </div>
          <button
            onClick={loadHealth}
            disabled={healthLoading}
            className="text-[10px] text-[color:var(--text-tertiary)] hover:text-[color:var(--text-primary)] flex items-center gap-1"
            data-testid="refresh-health"
          >
            <RotateCw size={11} className={healthLoading ? "animate-spin" : ""} />
            Uppdatera hälsa
          </button>
        </div>

        {/* Swedavia */}
        <SourceCard
          src={health?.sources?.swedavia}
          loading={healthLoading}
          dataTestId="source-swedavia"
        >
          {/* Keys-form för Swedavia */}
          {loading ? (
            <p className="text-xs text-[color:var(--text-tertiary)]">Läser in…</p>
          ) : (
            <div className="space-y-2 mt-2 pt-2 border-t border-[color:var(--border-subtle)]">
              <p className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">
                FlightInfo
              </p>
              <KeyField
                label="Primär"
                field="flightinfo_primary"
                info={{
                  set: config?.flightinfo?.primary_set,
                  masked: config?.flightinfo?.primary_masked,
                }}
                keys={keys}
                setKeys={setKeys}
              />
              <KeyField
                label="Sekundär"
                field="flightinfo_secondary"
                info={{
                  set: config?.flightinfo?.secondary_set,
                  masked: config?.flightinfo?.secondary_masked,
                }}
                keys={keys}
                setKeys={setKeys}
              />
              <p className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] mt-2">
                WaitTime
              </p>
              <KeyField
                label="Primär"
                field="waittime_primary"
                info={{
                  set: config?.waittime?.primary_set,
                  masked: config?.waittime?.primary_masked,
                }}
                keys={keys}
                setKeys={setKeys}
              />
              <KeyField
                label="Sekundär"
                field="waittime_secondary"
                info={{
                  set: config?.waittime?.secondary_set,
                  masked: config?.waittime?.secondary_masked,
                }}
                keys={keys}
                setKeys={setKeys}
              />
              <Button
                size="sm"
                onClick={saveKeys}
                disabled={savingKeys || !Object.values(keys).some((v) => v.trim())}
                data-testid="save-swedavia-keys"
              >
                {savingKeys ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
                Spara
              </Button>
            </div>
          )}
        </SourceCard>

        {/* adsb.lol */}
        <SourceCard
          src={health?.sources?.adsblol}
          loading={healthLoading}
          dataTestId="source-adsblol"
        />

        {/* OpenSky — OAuth2-credentials valfria (4000 anrop/dag vs 100) */}
        <SourceCard
          src={health?.sources?.opensky}
          loading={healthLoading}
          dataTestId="source-opensky"
          testServiceKeys={["opensky"]}
        >
          <div className="space-y-2 mt-2 pt-2 border-t border-[color:var(--border-subtle)]">
            {health?.sources?.opensky?.oauth_configured ? (
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-[color:var(--text-tertiary)]">
                  OAuth2 aktiv:{" "}
                  <span className="font-mono">{health?.sources?.opensky?.client_id}</span>
                  <span className="ml-2 text-[color:var(--success,#059669)] font-semibold">
                    4000/dag ✓
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={removeOpenskyCreds}
                  disabled={savingOpensky}
                  data-testid="opensky-remove-creds"
                >
                  <Trash2 size={12} className="mr-1" /> Ta bort
                </Button>
              </div>
            ) : (
              <p className="text-[11px] text-[color:var(--text-tertiary)]">
                Lägg till OAuth2-credentials för att höja från 100 → 4000 anrop/dag.
                Hämta dem på{" "}
                <a
                  href="https://opensky-network.org/my-opensky/account"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  opensky-network.org/my-opensky/account
                </a>
                {" "}→ API Clients.
              </p>
            )}
            <div className="space-y-2">
              <Input
                value={openskyClientId}
                onChange={(e) => setOpenskyClientId(e.target.value)}
                placeholder="Client ID (t.ex. emja-api-client)"
                className="h-9 text-sm font-mono"
                data-testid="opensky-client-id-input"
              />
              <div className="flex gap-2">
                <Input
                  type="password"
                  value={openskyClientSecret}
                  onChange={(e) => setOpenskyClientSecret(e.target.value)}
                  placeholder="Client Secret"
                  className="h-9 text-sm font-mono"
                  data-testid="opensky-client-secret-input"
                />
                <Button
                  size="sm"
                  onClick={saveOpenskyCreds}
                  disabled={savingOpensky || (!openskyClientId.trim() && !openskyClientSecret.trim())}
                  data-testid="opensky-save-creds"
                >
                  {savingOpensky ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                </Button>
              </div>
            </div>
          </div>
        </SourceCard>

        {/* AirLabs — Routes-DB + Airports-DB för Edit */}
        <SourceCard
          src={health?.sources?.airlabs}
          loading={healthLoading}
          dataTestId="source-airlabs"
          testServiceKeys={["airlabs"]}
        >
          <div className="space-y-1 mt-2 pt-2 border-t border-[color:var(--border-subtle)] text-[11px] text-[color:var(--text-tertiary)]">
            {health?.sources?.airlabs?.key_set ? (
              <p>API-nyckel aktiv — Edit kan svara på &quot;vilka flyg går till X?&quot; via Routes-DB.</p>
            ) : (
              <p>
                Ingen nyckel — sätt <code>AIRLABS_API_KEY</code> i backend/.env eller Railway-variables.
                Gratis konto på{" "}
                <a href="https://airlabs.co" target="_blank" rel="noopener noreferrer" className="underline">
                  airlabs.co
                </a>.
              </p>
            )}
          </div>
        </SourceCard>

        {/* AeroDataBox — webhook-baserad realtidsdata via RapidAPI */}
        <SourceCard
          src={health?.sources?.aerodatabox}
          loading={healthLoading}
          dataTestId="source-aerodatabox"
          testServiceKeys={["aerodatabox"]}
        >
          <AeroDataBoxKeys reloadHealth={loadHealth} />
        </SourceCard>

        {health?.checked_at && (
          <p className="text-[10px] text-[color:var(--text-tertiary)] text-right">
            Hälsa kontrollerad {new Date(health.checked_at).toLocaleTimeString("sv-SE")}
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
//  Sub-komponenter
// ============================================================================

function SourceCard({ src, loading, dataTestId, children, testServiceKeys }) {
  const [testing, setTesting] = useState({});

  const handleTest = async (svcKey) => {
    setTesting((t) => ({ ...t, [svcKey]: true }));
    try {
      const { data } = await api.post(`/flights/services/${svcKey}/test`);
      if (data.ok) {
        toast.success(`${svcKey}: OK`);
      } else {
        toast.error(`${svcKey}: ${data.note || "Fel"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test misslyckades");
    } finally {
      setTesting((t) => ({ ...t, [svcKey]: false }));
    }
  };

  if (!src && loading) {
    return (
      <div className="rounded border border-[color:var(--border-subtle)] p-3" data-testid={dataTestId}>
        <Loader2 size={14} className="animate-spin text-[color:var(--text-tertiary)]" />
      </div>
    );
  }
  if (!src) return null;

  const status = src.healthy === true ? "ok" : src.healthy === false ? "down" : "unknown";
  const StatusIcon = status === "ok" ? CircleCheck : status === "down" ? CircleX : CircleAlert;
  const statusColor = status === "ok" ? "#059669" : status === "down" ? "#DC2626" : "#D97706";
  const statusLabel = status === "ok" ? "Fungerar" : status === "down" ? "Nere/fel" : "Okänd";

  return (
    <div className="rounded border border-[color:var(--border-subtle)] p-3" data-testid={dataTestId}>
      {/* Header — namn + hälsa */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold">{src.name}</h4>
            {src.oauth_configured && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 uppercase tracking-wider">
                OAuth2 aktiv
              </span>
            )}
            {!src.key_required && !src.oauth_configured && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-[color:var(--bg-elevated)] text-[color:var(--text-tertiary)] uppercase tracking-wider">
                Ingen nyckel
              </span>
            )}
            {src.key_required && !src.configured && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-[color:var(--warning-bg,#FEF3C7)] text-[color:var(--warning-text,#92400E)] uppercase tracking-wider">
                Ej konfigurerad
              </span>
            )}
          </div>
          <p className="text-[11px] text-[color:var(--text-tertiary)] mt-1 leading-snug">
            {src.description}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 text-xs flex-shrink-0">
          <div className="flex items-center gap-1" style={{ color: statusColor }}>
            <StatusIcon size={14} />
            <span className="font-medium">{statusLabel}</span>
          </div>
          {(testServiceKeys || []).map((svcKey) => (
            <button
              key={svcKey}
              type="button"
              onClick={() => handleTest(svcKey)}
              disabled={testing[svcKey]}
              data-testid={`source-test-${svcKey}`}
              className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-[color:var(--accent-color,#6366f1)] text-white hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
            >
              {testing[svcKey]
                ? <Loader2 size={9} className="animate-spin" />
                : <RotateCw size={9} />}
              Testa {testServiceKeys.length > 1 ? svcKey.replace("swedavia_","") : ""}
            </button>
          ))}
        </div>
      </div>

      {/* Quota-rad — bara om vi har data */}
      {(src.calls_used_30d != null || src.calls_limit) && (
        <div className="mt-2 flex items-center gap-2 text-[10px] text-[color:var(--text-tertiary)]">
          {src.calls_limit ? (
            <>
              <span>
                {src.calls_used_30d?.toLocaleString("sv-SE") || 0}
                {" / "}
                {src.calls_limit.toLocaleString("sv-SE")} anrop senaste 30 dgr
              </span>
              <div className="flex-1 h-1 bg-[color:var(--bg-elevated)] rounded overflow-hidden">
                <div
                  className="h-full"
                  style={{
                    width: `${Math.min(100, ((src.calls_used_30d || 0) / src.calls_limit) * 100)}%`,
                    background: ((src.calls_used_30d || 0) / src.calls_limit) > 0.8 ? "#DC2626" : "#059669",
                  }}
                />
              </div>
            </>
          ) : (
            <span>{src.calls_used_30d?.toLocaleString("sv-SE") || 0} anrop senaste 30 dgr · ingen quota</span>
          )}
        </div>
      )}

      {/* Note (HTTP-status eller felmeddelande) */}
      {src.note && (
        <p className="mt-1 text-[10px] font-mono text-[color:var(--text-tertiary)]" style={{ color: status === "down" ? "#DC2626" : undefined }}>
          {src.note}
        </p>
      )}

      {/* Signup-länk om nyckel krävs */}
      {src.key_required && src.signup_url && (
        <a
          href={src.signup_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-2 text-[10px] underline text-[color:var(--text-secondary)]"
        >
          Skaffa nyckel <ExternalLink size={9} />
        </a>
      )}

      {children}
    </div>
  );
}

function KeyField({ label, field, info, keys, setKeys }) {
  return (
    <div>
      <label className="text-xs text-[color:var(--text-secondary)] block mb-1">
        {label}
        {info.set && (
          <span className="ml-2 text-[10px] text-[color:var(--success,#059669)]">
            ✓ Sparad ({info.masked})
          </span>
        )}
      </label>
      <Input
        type="password"
        value={keys[field]}
        onChange={(e) => setKeys({ ...keys, [field]: e.target.value })}
        placeholder={info.set ? "Ange ny för att rotera" : "Klistra in nyckel"}
        className="h-9 text-sm font-mono"
        data-testid={`swedavia-${field}-input`}
      />
    </div>
  );
}


// ============================================================================
//  AeroDataBoxKeys — flexibel lista av RapidAPI-nycklar med auto-rotation
// ============================================================================
function AeroDataBoxKeys({ reloadHealth }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [resubscribing, setResubscribing] = useState(false);
  const [resubResult, setResubResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/aerodatabox/config");
      setCfg(data);
    } catch {
      toast.error("Kunde inte läsa AeroDataBox-nycklar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addKey = async () => {
    const key = newKey.trim();
    if (key.length < 20) {
      toast.error("Nyckeln ser för kort ut");
      return;
    }
    setAdding(true);
    try {
      await api.post("/aerodatabox/config/add", {
        key,
        label: newLabel.trim() || null,
      });
      toast.success("Nyckel tillagd");
      setNewKey("");
      setNewLabel("");
      setShowAddForm(false);
      await load();
      reloadHealth?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte lägga till");
    } finally {
      setAdding(false);
    }
  };

  const removeKey = async (id) => {
    if (!window.confirm("Ta bort denna nyckel permanent?")) return;
    try {
      await api.post("/aerodatabox/config/remove", { key_id: id });
      toast.success("Nyckel borttagen");
      await load();
      reloadHealth?.();
    } catch {
      toast.error("Kunde inte ta bort");
    }
  };

  const resetQuota = async (id) => {
    try {
      await api.post("/aerodatabox/config/reset-quota", { key_id: id });
      toast.success("Kvotflagga nollställd");
      await load();
    } catch {
      toast.error("Kunde inte nollställa");
    }
  };

  const resubscribeAll = async () => {
    if (!window.confirm(
      "Registrera om webhooks för ALLA aktiva bevakningar mot den aktiva nyckeln?\n\n"
      + "Använd detta efter att du bytt nyckel eller om någon webhook tappats bort."
    )) return;
    setResubscribing(true);
    setResubResult(null);
    try {
      const { data } = await api.post("/aerodatabox/webhooks/resubscribe-all");
      setResubResult(data);
      if (data.ok > 0) {
        toast.success(`${data.ok}/${data.total} webhooks återregistrerade`);
      } else {
        toast.error(`Inga lyckade — ${data.fail}/${data.total} fail`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte återregistrera");
    } finally {
      setResubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-2 pt-2 border-t border-[color:var(--border-subtle)]">
        <Loader2 size={14} className="animate-spin text-[color:var(--text-tertiary)]" />
      </div>
    );
  }

  const keys = cfg?.keys || [];

  return (
    <div
      className="space-y-2 mt-2 pt-2 border-t border-[color:var(--border-subtle)]"
      data-testid="adb-keys-section"
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">
          RapidAPI-nycklar ({keys.length})
        </p>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px]"
            onClick={resubscribeAll}
            disabled={resubscribing}
            title="Registrera om alla webhooks på aktiv nyckel (efter nyckelbyte)"
            data-testid="adb-resubscribe-all"
          >
            {resubscribing ? (
              <Loader2 size={12} className="animate-spin mr-0.5" />
            ) : (
              <RotateCw size={12} className="mr-0.5" />
            )}
            Re-subscribe
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px]"
            onClick={() => setShowAddForm((s) => !s)}
            data-testid="adb-key-add-toggle"
          >
            <Plus size={12} className="mr-0.5" />
            Lägg till
          </Button>
        </div>
      </div>

      {/* Re-subscribe-resultat */}
      {resubResult && (
        <div
          className="rounded border border-[color:var(--border-subtle)] bg-emerald-50/40 p-2 text-[11px]"
          data-testid="adb-resubscribe-result"
        >
          <p className="font-semibold">
            Resultat: {resubResult.ok}/{resubResult.total} OK
            {resubResult.fail > 0 && ` · ${resubResult.fail} fail`}
          </p>
          {resubResult.fail > 0 && resubResult.details && (
            <details className="mt-1">
              <summary className="cursor-pointer text-[color:var(--text-tertiary)] text-[10px]">
                Visa fail-detaljer
              </summary>
              <ul className="mt-1 space-y-0.5 text-[10px] text-[color:var(--text-secondary)]">
                {resubResult.details
                  .filter((d) => !d.ok)
                  .slice(0, 10)
                  .map((d, i) => (
                    <li key={i} className="font-mono">
                      {d.flight_id}/{d.date}: {d.error}
                    </li>
                  ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Lista av befintliga nycklar */}
      {keys.length === 0 && !cfg?.env_fallback_set && (
        <p className="text-[11px] text-[color:var(--text-tertiary)] italic">
          Inga nycklar konfigurerade. Lägg till minst en för att aktivera AeroDataBox.
        </p>
      )}
      {keys.length === 0 && cfg?.env_fallback_set && (
        <p className="text-[11px] text-[color:var(--text-tertiary)]">
          Fallback från <code>.env</code> används:{" "}
          <span className="font-mono text-[10px]">{cfg.env_fallback_masked}</span>.
          Lägg till nycklar nedan för rotation.
        </p>
      )}
      <ul className="space-y-1.5" data-testid="adb-keys-list">
        {keys.map((k) => {
          const isExhausted = !!k.quota_exhausted_at;
          return (
            <li
              key={k.id}
              className={`rounded border p-2 text-[11px] ${
                k.is_active
                  ? "border-emerald-300 bg-emerald-50/40"
                  : isExhausted
                  ? "border-amber-300 bg-amber-50/40"
                  : "border-[color:var(--border-subtle)] bg-white"
              }`}
              data-testid={`adb-key-row-${k.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px]">{k.masked}</span>
                    {k.is_active && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 uppercase tracking-wider">
                        Aktiv
                      </span>
                    )}
                    {isExhausted && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 uppercase tracking-wider">
                        Kvot slut
                      </span>
                    )}
                  </div>
                  {k.label && (
                    <p className="text-[10px] text-[color:var(--text-secondary)] mt-0.5">
                      {k.label}
                    </p>
                  )}
                  {k.added_at && (
                    <p className="text-[10px] text-[color:var(--text-tertiary)] mt-0.5">
                      Tillagd {new Date(k.added_at).toLocaleDateString("sv-SE")}
                      {isExhausted && (
                        <> · Slut sedan {new Date(k.quota_exhausted_at).toLocaleDateString("sv-SE")}</>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isExhausted && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-1.5 text-[10px]"
                      onClick={() => resetQuota(k.id)}
                      title="Nollställ kvotflagga (om månadskvot resetat)"
                      data-testid={`adb-key-reset-${k.id}`}
                    >
                      <RotateCw size={10} />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-1.5 text-[10px] text-red-600 hover:bg-red-50"
                    onClick={() => removeKey(k.id)}
                    title="Ta bort"
                    data-testid={`adb-key-remove-${k.id}`}
                  >
                    <Trash2 size={10} />
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Add-form */}
      {showAddForm && (
        <div
          className="rounded border border-dashed border-[color:var(--border-subtle)] p-2 space-y-2 bg-[color:var(--bg-elevated)]"
          data-testid="adb-key-add-form"
        >
          <Input
            type="password"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="RapidAPI-nyckel (50 tecken)"
            className="h-9 text-sm font-mono"
            data-testid="adb-key-input"
          />
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Etikett (valfritt — t.ex. 'Backup-konto')"
            className="h-9 text-sm"
            data-testid="adb-key-label-input"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={addKey}
              disabled={adding || newKey.trim().length < 20}
              data-testid="adb-key-save"
            >
              {adding ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}
              Spara nyckel
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowAddForm(false);
                setNewKey("");
                setNewLabel("");
              }}
            >
              Avbryt
            </Button>
          </div>
          <p className="text-[10px] text-[color:var(--text-tertiary)]">
            Skaffa fler nycklar på{" "}
            <a
              href="https://rapidapi.com/aedbx-aedbx/api/aerodatabox"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              RapidAPI → AeroDataBox
            </a>
            {" "}(BASIC är gratis: 500 anrop/månad per nyckel).
          </p>
        </div>
      )}

      <p className="text-[10px] text-[color:var(--text-tertiary)] pt-1 border-t border-[color:var(--border-subtle)]">
        Vid 429 MONTHLY-quota markeras nyckeln automatiskt som slut och systemet
        roterar till nästa giltiga. Manuell reset via{" "}
        <RotateCw size={9} className="inline" />-knappen.
      </p>
    </div>
  );
}
